import { useState, useEffect, useMemo } from 'react';

// material-ui
import {
  Grid,
  Stack,
  useMediaQuery,
  Theme,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  Box,
  Slide,
  Pagination,
  Typography
} from '@mui/material';

// project import
import EmptyUserCard from 'components/cards/skeleton/EmptyUserCard';
import PatientCard from 'components/cards/PatientCard';

import { GlobalFilter } from 'utils/react-table';
import { getUserProviderName } from 'helpers/UserHelper';

// types
import { IUser, UserProviderId, UserRole } from 'models/IUser';

// assets
import { useAppSelector } from 'app/hooks';
import { useGetExternalDevicesQuery, useGetUsersQuery } from 'features/api/apiSlice';
import { selectCurrentUser } from 'features/auth/authSlice';
import { useTranslation } from 'react-i18next';
import i18n from 'app/i18n';

// ==============================|| CUSTOMER - CARD ||============================== //

const PatientsPage = () => {
  const { t } = useTranslation();
  const matchDownSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  type SortKey = 'lastName' | 'firstName' | 'enrolment' | 'lastMeasurement' | 'lastSync';

  const authUser = useAppSelector(selectCurrentUser);
  const { data: users, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authUser!!.id!!, role: UserRole.Patient })
  const { data: externalDevices, isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetExternalDevicesQuery({})
  const isEXCAREAdmin = authUser?.role === UserRole.Admin && authUser?.provider === UserProviderId.EXCARE;

  const [sortBy, setSortBy] = useState<SortKey>('lastName');
  const [providerFilters, setProviderFilters] = useState<string[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [usersToShow, setUsersToShow] = useState<IUser[]>([]);
  const [page, setPage] = useState(1);
  const handleSortBy = (event: SelectChangeEvent) => {
    handleChangePage(null, 1)
    setSortBy(event.target.value as SortKey);
  };

  const handleProviderFilter = (event: SelectChangeEvent<string[]>) => {
    handleChangePage(null, 1);
    const value = event.target.value;
    const selectedProviders = typeof value === 'string' ? value.split(',') : value;
    setProviderFilters(selectedProviders);
  };

  // search
  useEffect(() => {
    if (users !== undefined && users !== null) {
      const newData = users.filter((value: any) => {
        const matchesProvider = providerFilters.length === 0 || providerFilters.includes(value.provider);

        if (!matchesProvider) {
          return false;
        }

        if (globalFilter) {
          return value.firstName.toLowerCase().includes(globalFilter.toLowerCase())
            || value.lastName.toLowerCase().includes(globalFilter.toLowerCase());
        } else {
          return value;
        }
      });
      setUsersToShow(newData);
    }
  }, [globalFilter, providerFilters, users]);

  const patientsPerPage = 8;
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(patientsPerPage);

  const count = Math.ceil(usersToShow.length / patientsPerPage);

  const handleChangePage = (event: any, page: any) => {
    setPage(page);
    setMinValue((page - 1) * patientsPerPage);
    setMaxValue(page * patientsPerPage);
  };

  const allColumns = [
    {
      id: 1,
      key: 'lastName' as SortKey,
      header: t('last_name', 'Last name')
    },
    {
      id: 2,
      key: 'firstName' as SortKey,
      header: t('first_name', 'First name')
    },
    {
      id: 3,
      key: 'enrolment' as SortKey,
      header: t('enrolment', 'Enrolment')
    },
    {
      id: 4,
      key: 'lastMeasurement' as SortKey,
      header: t('last_measurement', 'Last measurement')
    },
    {
      id: 5,
      key: 'lastSync' as SortKey,
      header: t('last_synced', 'Last synced')
    }
  ];

  const selectedSortHeader = allColumns.find((column) => column.key === sortBy)?.header ?? t('sort_by', 'Sort by');

  const compareLocale = (a?: string, b?: string) =>
    String(a ?? '').localeCompare(String(b ?? ''), i18n.language, { sensitivity: 'base' });

  const compareDateDesc = (a?: Date, b?: Date) =>
    (b ? new Date(b).getTime() : 0) - (a ? new Date(a).getTime() : 0);

  const sortedUsersToShow = useMemo(() => {
    return [...usersToShow].sort((a, b) => {
      if (sortBy === 'firstName') return compareLocale(a.firstName, b.firstName);
      if (sortBy === 'lastName') return compareLocale(a.lastName, b.lastName);
      if (sortBy === 'enrolment') return compareDateDesc(a.enrolmentAt, b.enrolmentAt);
      if (sortBy === 'lastMeasurement') return compareDateDesc(a.lastValueAt, b.lastValueAt);
      if (sortBy === 'lastSync') return compareDateDesc(a.lastSyncedAt, b.lastSyncedAt);
      return 0;
    });
  }, [usersToShow, sortBy]);

  const providers = Array.from(new Set((users ?? []).map((user) => user.provider).filter((provider): provider is string => Boolean(provider))));

  if (isLoadingUsers || isLoadingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('loading', 'Loading...')} />
      </Grid>
    )
  } else if (isFetchingUsers || isFetchingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('fetching', 'Fetching...')} />
      </Grid>
    )
  } else {
    return (
      <>
        <Box sx={{ position: 'relative', marginBottom: 3 }}>
          <Stack direction="row" alignItems="center">
            <Stack
              direction={matchDownSM ? 'column' : 'row'}
              sx={{ width: '100%' }}
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <GlobalFilter preGlobalFilteredRows={usersToShow as any} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
              <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={1}>
                {isEXCAREAdmin &&
                  <FormControl sx={{ m: 1, minWidth: 180 }}>
                    <Select
                      multiple
                      displayEmpty
                      value={providerFilters}
                      onChange={handleProviderFilter}
                      inputProps={{ 'aria-label': 'Filter by provider' }}
                      renderValue={(selected) => {
                        const selectedProviders = selected as string[];

                        if (!selectedProviders.length)
                          return <Typography variant="subtitle2">{t('provider', 'Provider')}</Typography>;

                        return <Typography variant="subtitle2">{t('provider', 'Provider')} ({selectedProviders.length})</Typography>;
                      }}
                    >
                      {providers.map((provider) => (
                        <MenuItem key={provider} value={provider}>
                          <Checkbox checked={providerFilters.indexOf(provider) > -1} color="primary" />
                          <ListItemText primary={getUserProviderName(provider)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                }
                <FormControl sx={{ m: 1, minWidth: 120 }}>
                  <Select
                    value={sortBy}
                    onChange={handleSortBy}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    renderValue={(selected) => {
                      if (!selected)
                        return <Typography variant="subtitle1">{t('sort_by', 'Sort by')}</Typography>;
  
                      return <Typography variant="subtitle2">{t('sort_by', 'Sort by')} ({selectedSortHeader})</Typography>;
                    }}
                  >
                    {allColumns.map((column) => {
                      return (
                        <MenuItem key={column.id} value={column.key}>
                          {column.header}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Grid container spacing={3}>
          {usersToShow.length > 0 ? (
            sortedUsersToShow
              .slice(minValue, maxValue)
              .map((user: IUser, index: number) => (
                <Slide key={index} direction="up" in={true} timeout={50}>
                  <Grid item xs={12} sm={5} lg={3}>
                    <PatientCard patient={user} externalDevices={externalDevices!!.filter(externalDevice => externalDevice.assignedUserId == user.id)!!} />
                  </Grid>
                </Slide>
              ))
          ) : (
            <EmptyUserCard title={t('no_users_to_show', 'There are no users to show.')} />
          )}
        </Grid>
        <Stack spacing={2} sx={{ p: 2.5 }} alignItems="flex-end">
          <Pagination
            count={count}
            size="medium"
            page={page}
            showFirstButton
            showLastButton
            variant="combined"
            color="primary"
            onChange={handleChangePage}
          />
        </Stack>
      </>
    );
  }
};

export default PatientsPage;
